/**
 * OEM Search Engine — native V3 color search for the DYC theme.
 * Replaces the mcc-dyc.vercel.app iframe (see docs/superpowers/specs/2026-07-14-native-oem-search-design.md).
 * Pure logic is exported for node --test (tests/oem-search-engine.test.mjs).
 */
(function () {
  'use strict';
  var isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
  if (isBrowser && window.__OEMSearchEngineInitialized) return;
  if (isBrowser) window.__OEMSearchEngineInitialized = true;

  /* ── Config (set by section/layout via window.OEMSearchConfig) ───── */
  function config() {
    var c = (isBrowser && window.OEMSearchConfig) || {};
    return {
      apiBase: c.apiBase || 'https://search.mycarcolor.com',
      apiKey: c.apiKey || '',
      storefrontToken: c.storefrontToken || '',
      decodeEndpoint: c.decodeEndpoint || 'https://dyc-ai-search.vercel.app/api/vehicle-decode',
      // VIN → factory OEM colour decode (Partslink24 → colour code), dyc-analytics.
      // GET {vinDecodeEndpoint}/{VIN} → { query, vehicle, best, colours }. The route
      // allow-lists the dipyourcar.com / oemcolors.com origins (no key needed in the browser).
      vinDecodeEndpoint: c.vinDecodeEndpoint || 'https://dyc-analytics.vercel.app/api/partslink/vin',
      // Unified vehicle decode (VIN or plate): VehicleDatabases identity + Partslink exact
      // colour. POST {type:"vin",vin} | {type:"plate",plate,state} → { vin, make, model, year,
      // partslink?:{ vehicle, best, query } }. Origin-allow-listed (no key needed in browser).
      vehicleDecodeEndpoint: c.vehicleDecodeEndpoint || 'https://dyc-analytics.vercel.app/api/vehicle/decode',
      // OCR: multipart photo of a plate/VIN → { plate, state } | { vin }. Same origin gate.
      vehicleOcrEndpoint: c.vehicleOcrEndpoint || 'https://dyc-analytics.vercel.app/api/vehicle/ocr',
      searchPageUrl: c.searchPageUrl || '/pages/find-your-color',
      fallbackHandle: c.fallbackHandle || 'luna-oem-basecoat-paint-search',
      pageSize: c.pageSize || 24,
      // Task 20 (owner wave 2, #4): license-plate kill switch. Defaults true (opt-out,
      // not opt-in) so existing installs/tests with no OEMSearchConfig.plateEnabled set
      // keep today's behavior; theme.liquid only sends `false` explicitly.
      plateEnabled: c.plateEnabled !== false
    };
  }

  /* ── Finish derivation ───────────────────────────────────────────── */
  function deriveFinish(hit) {
    if (hit.is_xirallic) return 'Xirallic';
    if (hit.is_candi) return 'Candy';
    if (hit.is_pearl) return 'Pearl';
    if (hit.is_metallic) return 'Metallic';
    if (hit.is_special_effect) return 'Special';
    return 'Solid';
  }

  /* ── English color-name picker (port of dyc-analytics mcc-v3.ts) ── */
  // Ported from /Volumes/X10 Pro/dyc-analytics/src/lib/chat/mcc-v3.ts lines 74–144.
  //
  // MCC returns color_name in the manufacturer's native language — German for
  // BMW / Audi / Mercedes / VW / Porsche ("Isle of Man Grün", "Schwarz"),
  // which the voice TTS mangles (reads "GRUEN" phonetically) and customers
  // don't recognize. MCC also returns `alternative_color_names`, which usually
  // includes an English variant ("Isle of Man Green"). Prefer the most
  // English-readable candidate; fall back to translating German color tokens
  // inline when no clean English alternative exists. Applies to every surface
  // (voice + messaging + web find_color) since it sits at the normalize layer.
  var ENGLISH_COLOR_WORDS = [
    'green', 'blue', 'red', 'black', 'white', 'silver', 'grey', 'gray',
    'yellow', 'brown', 'gold', 'orange', 'purple', 'pink', 'beige',
    'bronze', 'copper', 'violet', 'turquoise'
  ];
  // Umlaut/ß or common German color words.
  var GERMAN_MARKER_RE =
    /[äöüß]|\b(gr[üu]e?n|gruene?|schwarz|wei(?:ss|ß)|blau|rot|gelb|silber|grau|braun|violett|rosa|t[üu]rkis)\b/i;
  // Spanish variants MCC sometimes returns ("Verde …", "Rojo …").
  var SPANISH_MARKER_RE =
    /\b(verde|rojo|azul|negro|blanco|gris|amarillo|plata|marr[óo]n|dorado|naranja)\b/i;
  // Code-ish blobs like "BMWC4G" — a letter run followed by a digit. NOT a
  // plain all-caps word (so "RACING GREEN" isn't penalised).
  var CODEY_RE = /\b[A-Z]{2,}\d[A-Z0-9]*\b/;
  var GERMAN_COLOR_TOKENS = {
    gruen: 'Green', 'grün': 'Green', grun: 'Green', gruene: 'Green', 'grüne': 'Green',
    schwarz: 'Black', weiss: 'White', 'weiß': 'White',
    blau: 'Blue', rot: 'Red', gelb: 'Yellow', silber: 'Silver',
    grau: 'Grey', braun: 'Brown', gold: 'Gold', orange: 'Orange',
    violett: 'Purple', rosa: 'Pink', 'türkis': 'Turquoise', turkis: 'Turquoise'
  };

  function hasEnglishColorWord(s) {
    var lower = s.toLowerCase();
    return ENGLISH_COLOR_WORDS.some(function (w) {
      return new RegExp('\\b' + w + '\\b').test(lower);
    });
  }

  /** Choose the most English-readable color name from the primary + MCC's
   *  alternative_color_names. Scores out German/Spanish words and code-blobs,
   *  rewards a recognisable English color word, prefers concise. If every
   *  candidate is German, translates the German color tokens inline. */
  function pickEnglishColorName(primary, alternatives, code) {
    var candidates = [primary].concat(alternatives)
      .map(function (s) { return (s == null ? '' : s).trim(); })
      .filter(Boolean);
    if (candidates.length === 0) return primary;
    var best = candidates[0];
    var bestScore = -Infinity;
    candidates.forEach(function (c, idx) {
      var score = 0;
      // The PRIMARY name (candidates[0]) is MCC's canonical name for THIS color.
      // Strongly prefer it — an alternative name should only win when the primary
      // is genuinely bad (German/Spanish/codey). Without this, an alternative
      // that happened to be a hair shorter displaced the right name and flipped
      // the COLOR's meaning: FORD JM "Venetian Red" (a red) rendered as the
      // alternative "Bright Blue" (caught 2026-06-05). The +30 sits below the
      // German/Spanish penalties so a German primary still gets replaced.
      if (idx === 0) score += 30;
      if (GERMAN_MARKER_RE.test(c)) score -= 100;
      if (SPANISH_MARKER_RE.test(c)) score -= 40;
      if (code && c.toUpperCase().indexOf(code.toUpperCase()) !== -1) score -= 30;
      if (CODEY_RE.test(c)) score -= 25;
      if (hasEnglishColorWord(c)) score += 20;
      score -= c.length * 0.1; // tie-break toward the concise option
      if (score > bestScore) {
        bestScore = score;
        best = c;
      }
    });
    if (GERMAN_MARKER_RE.test(best)) {
      return best.replace(/[A-Za-zÀ-ÿäöüßÄÖÜ]+/g, function (w) {
        var t = GERMAN_COLOR_TOKENS[w.toLowerCase()];
        return t != null ? t : w;
      });
    }
    return best;
  }

  /* ── GID helper ──────────────────────────────────────────────────── */
  var GID_RE = /\/(\d+)$/;
  function gidToNumericId(gid) {
    if (!gid) return null;
    var m = GID_RE.exec(String(gid));
    return m ? m[1] : null;
  }

  /* ── Normalize a raw V3 hit ──────────────────────────────────────── */
  function normalizeHit(hit) {
    var e = hit.enrichment || null;
    return {
      colorId: hit.color_id,
      brandName: hit.brand_name,
      colorCode: hit.color_code,
      colorName: pickEnglishColorName(hit.color_name, hit.alternative_color_names || [], hit.color_code),
      alternativeNames: hit.alternative_color_names || [],
      // Task 24c: under the server grouping contract (group=true, the server
      // default — docs/shopify-grouping-and-variants-explainer.md) this is
      // the union of every code the same paint is sold under across its
      // group (e.g. the Nardo Grey card lists LY7C, T3, Y7C…). Drives the
      // cards' "Also: …" line + the expand-panel alias list.
      alternativeCodes: hit.alternative_color_codes || [],
      year: hit.year || '',
      rgbHex: hit.rgb_hex || null,
      imageUrl: hit.image_url || null,
      isMetallic: !!hit.is_metallic, isPearl: !!hit.is_pearl,
      isXirallic: !!hit.is_xirallic, isCandi: !!hit.is_candi,
      isSpecialEffect: !!hit.is_special_effect,
      finish: deriveFinish(hit),
      // Paint-group passthrough (Task 24/24c): the server grouping contract
      // (docs/shopify-grouping-and-variants-explainer.md) documents
      // paint_group_id/paint_group_size on hits, but the live serializer
      // still doesn't emit them (probed 2026-07-17) — and per that doc the
      // storefront no longer needs them (group=true collapses server-side).
      // Kept as inert future-proofing with "no group info" defaults. Per
      // spec the group id is opaque/rebuildable — never persist it.
      paintGroupId: hit.paint_group_id != null ? hit.paint_group_id : null,
      paintGroupSize: hit.paint_group_size != null ? hit.paint_group_size : 1,
      isVariant: !!hit.is_variant,
      vehicles: (hit.vehicle_compatibility || []).map(function (v) {
        return { modelName: v.model_name, years: (v.years || []).slice().sort(function (a, b) { return a - b; }) };
      }),
      enrichment: e ? {
        cloudId: e.cloud_id != null ? e.cloud_id : null,
        hasFormula: !!e.has_formula,
        pricing: e.pricing || null,
        shopifyProductGid: e.shopify_product_gid || null,
        shopifyProductHandle: e.shopify_product_handle || null, // V3 addition; null until service ships it
        shopifyVariantGids: (e.shopify_variant_gids || []).map(function (v) {
          return { id: v.id, title: v.title || '' };
        }),
        undercoat: e.undercoat || null
      } : null
    };
  }

  /* ── URL builders ────────────────────────────────────────────────── */
  function utmQuery(surface, colorCode) {
    return 'utm_source=oem_search&utm_medium=' + encodeURIComponent(surface || 'search_page')
      + '&utm_campaign=mcc_search&utm_content=' + encodeURIComponent(colorCode || '');
  }

  function buildProductUrl(color, opts) {
    opts = opts || {};
    var e = color.enrichment || {};
    var handle = opts.handle || e.shopifyProductHandle;
    if (!handle) return null;
    var url = '/products/' + handle;
    var firstVariant = (e.shopifyVariantGids && e.shopifyVariantGids[0]) || null;
    var variantId = firstVariant ? gidToNumericId(firstVariant.id) : null;
    url += '?' + (variantId ? 'variant=' + variantId + '&' : '') + utmQuery(opts.surface, color.colorCode);
    return url;
  }

  function base64Encode(str) {
    if (isBrowser && typeof btoa === 'function') {
      return btoa(unescape(encodeURIComponent(str)));
    }
    return Buffer.from(str, 'utf8').toString('base64');
  }

  function buildFallbackUrl(color, surface) {
    // Payload MUST match what mcc.js sends today (redirectToFallbackProduct,
    // assets/mcc.js ~line 424) so the fallback PDP keeps working:
    // - imgSrc is a CSS background VALUE — the receiver (~line 1213) sets
    //   style.background = imgSrc + ' center center / cover', which works for
    //   both url('...') and a bare hex color.
    // - priceCode comes from enrichment pricing price_group (non_reduced first).
    var pricing = color.enrichment && color.enrichment.pricing;
    var priceCode = (pricing && pricing.non_reduced && pricing.non_reduced.price_group)
      || (pricing && pricing.reduced && pricing.reduced.price_group)
      || '';
    var payload = {
      color: {
        code: color.colorCode, name: color.colorName, brand: color.brandName,
        year: color.year,
        imgSrc: color.imageUrl ? "url('" + color.imageUrl + "')" : (color.rgbHex || ''),
        priceCode: priceCode
      },
      selectedSize: null
    };
    return '/products/' + config().fallbackHandle
      + '?color=' + encodeURIComponent(base64Encode(JSON.stringify(payload)))
      + '&' + utmQuery(surface, color.colorCode);
  }

  var PARAM_KEYS = { tool: 'tool', q: 'q', brandId: 'brand_id', modelId: 'model_id', year: 'year', make: 'make', model: 'model' };
  function buildSearchPageUrl(base, params) {
    var parts = [];
    Object.keys(PARAM_KEYS).forEach(function (k) {
      if (params && params[k]) parts.push(PARAM_KEYS[k] + '=' + encodeURIComponent(params[k]));
    });
    return base + (parts.length ? '?' + parts.join('&') : '');
  }
  function paramsFromSearch(searchString) {
    var sp = new URLSearchParams(searchString || '');
    return {
      tool: sp.get('tool') || '', q: sp.get('q') || '',
      brandId: sp.get('brand_id') || '', modelId: sp.get('model_id') || '',
      year: sp.get('year') || '', make: sp.get('make') || '', model: sp.get('model') || ''
    };
  }

  /* ── Fetch layer ─────────────────────────────────────────────────── */
  var _fetch = function () { return fetch.apply(null, arguments); };
  function _setFetch(fn) { _fetch = fn; } // test seam

  var API_CACHE = new Map(); // url → { data, expiresAt }
  var CACHE_TTL_MS = 5 * 60 * 1000;
  function clearApiCache() { API_CACHE.clear(); }

  function mccHeaders() {
    return { accept: 'application/json', Authorization: 'Bearer ' + config().apiKey };
  }

  function fetchJson(url, opts) {
    opts = opts || {};
    var maxRetries = opts.retries != null ? opts.retries : 3;
    var attempt = 0;
    function tryOnce() {
      return _fetch(url, { headers: mccHeaders(), signal: opts.signal }).then(function (res) {
        if (res.ok) return res.json();
        if (opts.emptyOn404 && res.status === 404) return null;
        if ((res.status >= 500 || res.status === 429) && attempt < maxRetries - 1) {
          attempt++;
          return new Promise(function (r) { setTimeout(r, 400 * Math.pow(2, attempt - 1)); }).then(tryOnce);
        }
        var err = new Error('OEM search HTTP ' + res.status);
        err.status = res.status;
        throw err;
      });
    }
    return tryOnce();
  }

  function cachedFetchJson(url, opts) {
    var hit = API_CACHE.get(url);
    if (hit && Date.now() < hit.expiresAt) return Promise.resolve(hit.data);
    return fetchJson(url, opts).then(function (data) {
      if (API_CACHE.size > 200) API_CACHE.clear();
      API_CACHE.set(url, { data: data, expiresAt: Date.now() + CACHE_TTL_MS });
      return data;
    });
  }

  function searchColors(params, signal) {
    params = params || {};
    var qs = new URLSearchParams();
    qs.set('q', params.q || '');
    if (params.brandId) qs.set('brand_id', params.brandId);
    if (params.modelId) qs.set('model_id', params.modelId);
    if (params.year) qs.set('year', String(params.year));
    if (params.onlyMetallic) qs.set('only_metallic', 'true');
    if (params.onlyPearl) qs.set('only_pearl', 'true');
    qs.set('page', String(params.page || 1));
    qs.set('page_size', String(params.pageSize || config().pageSize));
    // Task 24c: server-side grouping contract (docs/shopify-grouping-and-
    // variants-explainer.md, live since 2026-07-17). group=true — the server
    // default, sent EXPLICITLY to self-document — is the browse list: one
    // card per paint, with alternative_color_codes/names carrying the whole
    // group's alias union. Callers that need record-level fidelity pass
    // group:false (the drill-down set — e.g. the PDP characteristics lookup
    // in oem-pdp-blocks.js, which matches by exact primary code). The param
    // is part of the URL and therefore of the API_CACHE key — grouped and
    // ungrouped result sets can never cross-pollute the cache.
    qs.set('group', params.group === false ? 'false' : 'true');
    var url = config().apiBase + '/colors/search/?' + qs.toString();
    return cachedFetchJson(url, { signal: signal }).then(function (json) {
      json = json || {};
      var total = json.total_hits != null ? json.total_hits : (json.total || 0);
      return {
        colors: (json.hits || []).map(normalizeHit),
        total: total,
        page: json.page || params.page || 1,
        pageSize: json.page_size || params.pageSize || config().pageSize,
        totalPages: json.total_pages || Math.max(1, Math.ceil(total / (json.page_size || config().pageSize)))
      };
    });
  }

  function getMakes() {
    return cachedFetchJson(config().apiBase + '/motomix/makes').then(function (data) {
      var list = Array.isArray(data) ? data : ((data && data.makes) || []);
      return list.map(function (m) { return { brandId: String(m.brand_id), brandName: m.brand_name }; });
    });
  }
  function getModels(brandId) {
    return cachedFetchJson(config().apiBase + '/motomix/makes/' + encodeURIComponent(brandId) + '/models').then(function (data) {
      var list = Array.isArray(data) ? data : ((data && data.models) || []);
      return list.map(function (m) { return { modelId: String(m.model_id), modelName: m.model_name }; });
    });
  }
  function getYears(brandId, modelId) {
    var url = config().apiBase + '/motomix/makes/' + encodeURIComponent(brandId)
      + '/models/' + encodeURIComponent(modelId) + '/years';
    return cachedFetchJson(url, { emptyOn404: true }).then(function (data) {
      if (!data) return [];
      var years = Array.isArray(data) ? data : (data.years || []);
      return years.slice().sort(function (a, b) { return b - a; });
    });
  }

  /* ── Storefront handle resolver (fallback until V3 ships handles) ── */
  var HANDLE_CACHE = new Map(); // productGid → handle

  function resolveHandles(gids) {
    var missing = gids.filter(function (g) { return g && !HANDLE_CACHE.has(g); });
    if (!missing.length) return Promise.resolve(HANDLE_CACHE);
    var query = 'query($ids:[ID!]!){nodes(ids:$ids){id ... on Product{handle}}}';
    return _fetch('/api/2025-07/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config().storefrontToken
      },
      body: JSON.stringify({ query: query, variables: { ids: missing } })
    }).then(function (res) {
      if (!res.ok) throw new Error('Storefront HTTP ' + res.status);
      return res.json();
    }).then(function (json) {
      ((json.data && json.data.nodes) || []).forEach(function (n) {
        if (n && n.id && n.handle) HANDLE_CACHE.set(n.id, n.handle);
      });
      return HANDLE_CACHE;
    });
  }

  function resolveProductUrl(color, opts) {
    opts = opts || {};
    var direct = buildProductUrl(color, opts);
    if (direct) return Promise.resolve(direct);
    var gid = color.enrichment && color.enrichment.shopifyProductGid;
    if (!gid) return Promise.resolve(buildFallbackUrl(color, opts.surface));
    return resolveHandles([gid]).then(function (map) {
      var handle = map.get(gid);
      if (handle) return buildProductUrl(color, { surface: opts.surface, handle: handle });
      return buildFallbackUrl(color, opts.surface);
    }).catch(function () {
      return buildFallbackUrl(color, opts.surface);
    });
  }

  /* ── Results rendering (pure — node-testable) ─────────────────────── */
  // Markup/classes lifted verbatim from the owner-approved visual prototype
  // (templates/oem-search-prototype.html, cardHTML()/priceHTML()/modelsLine())
  // — osr-* class names + content layout are the approved visual language.
  // Field names are the NormalizedColor shape (camelCase) that normalizeHit()
  // above produces, not the raw V3 snake_case hit.
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // CSS-url safety (Task 1 review finding): HTML-escaping alone is NOT safe
  // inside a CSS url('...') string — the browser HTML-decodes the attribute
  // value BEFORE the CSS parser ever sees it, so an html-escaped quote
  // (&#39;) round-trips back into a literal ' and still breaks out of the
  // url() string. Percent-encode the CSS-meaningful characters first so the
  // decoded attribute value is still a safe CSS token, then HTML-escape the
  // (now quote-free) result for safe attribute embedding.
  function cssUrlEncode(url) {
    return String(url == null ? '' : url)
      .replace(/'/g, '%27').replace(/\(/g, '%28').replace(/\)/g, '%29');
  }

  function swatchStyle(color) {
    var hex = escapeHtml(color.rgbHex || '#cccccc');
    var style = '--sw:' + hex;
    if (color.imageUrl) {
      style += ";background-image:url('" + escapeHtml(cssUrlEncode(color.imageUrl)) + "')";
    }
    return style;
  }

  var FINISH_ICON = { Metallic: 'sparkles', Pearl: 'droplet', Xirallic: 'sparkles', Candy: 'candy', Special: 'sparkles', Solid: 'square' };

  function vehicleYearsText(years) {
    if (!years || !years.length) return '';
    var lo = Math.min.apply(null, years);
    var hi = Math.max.apply(null, years);
    return lo === hi ? String(lo) : (lo + '–' + hi);
  }

  function yearRangeText(color) {
    var ys = [];
    (color.vehicles || []).forEach(function (v) {
      (v.years || []).forEach(function (y) { ys.push(y); });
    });
    if (!ys.length) return color.year || '';
    return vehicleYearsText(ys);
  }

  // IDs only ever come from our own generated string (surface + index), but
  // sanitize defensively since it lands in an aria-controls/id pair.
  function safeIdPart(s) {
    return String(s || '').replace(/[^a-zA-Z0-9_-]/g, '');
  }

  // Task 24c: alias helpers for grouped cards. Under the server grouping
  // contract a rep card's alternative_color_codes/names carry the union of
  // every code/name in its paint group. Both helpers dedupe (case-
  // insensitively) against the card's own displayed code/name and against
  // each other, preserving server order. Pure → node-testable.
  function aliasCodes(color) {
    var own = String(color && color.colorCode != null ? color.colorCode : '').toUpperCase();
    var seen = {};
    var out = [];
    ((color && color.alternativeCodes) || []).forEach(function (code) {
      var c = String(code == null ? '' : code).trim();
      if (!c) return;
      var key = c.toUpperCase();
      if (key === own || seen[key]) return;
      seen[key] = true;
      out.push(c);
    });
    return out;
  }
  function aliasNames(color) {
    var shownName = String(color && color.colorName != null ? color.colorName : '').toUpperCase();
    var seen = {};
    var out = [];
    ((color && color.alternativeNames) || []).forEach(function (name) {
      var n = String(name == null ? '' : name).trim();
      if (!n) return;
      var key = n.toUpperCase();
      if (key === shownName || seen[key]) return;
      seen[key] = true;
      out.push(n);
    });
    return out;
  }
  // Card drawers (owner wave 3): the card face carries just brand + name — the
  // data that used to crowd it (compat models line, "Also:" alias line, the
  // code/year chips) lives in TWO overlay drawers opened from icon buttons in
  // the card foot. Both drawers reuse the .osr-vehicles-panel overlay + the
  // .osr-vehicles-more toggle + .osr-vehicles-close classes, so every existing
  // open/close/Escape/focus/a11y behavior carries over untouched (the toggle ↔
  // panel link is aria-controls, which supports any number of pairs per card).
  function drawerPanelHTML(panelId, title, items) {
    return '<div class="osr-vehicles-panel" id="' + panelId + '" role="group" aria-label="' + escapeHtml(title) + '" hidden>'
      +   '<div class="osr-vehicles-panel-head">'
      +     '<span class="osr-vehicles-panel-title">' + escapeHtml(title) + '</span>'
      +     '<button type="button" class="osr-vehicles-close" aria-label="Close"><i data-lucide="x"></i></button>'
      +   '</div>'
      +   '<ul class="osr-vehicles-list">' + items + '</ul>'
      + '</div>';
  }

  // Drawer 1 — compatible vehicles (car icon). null when the color carries no
  // vehicle data: the icon is omitted rather than opening an empty drawer (the
  // brand is already on the card face).
  function vehiclesDrawerHTML(vehicles, index, surface) {
    vehicles = vehicles || [];
    if (!vehicles.length) return null;
    var panelId = 'osr-veh-' + safeIdPart(surface) + '-' + index;
    var items = vehicles.map(function (v) {
      return '<li><b>' + escapeHtml(v.modelName) + '</b> <span class="osr-vehicle-years">' + escapeHtml(vehicleYearsText(v.years)) + '</span></li>';
    }).join('');
    return { id: panelId, html: drawerPanelHTML(panelId, 'Compatible vehicles', items) };
  }

  // Drawer 2 — colour identity (tag icon): primary code + years, then the
  // also-known-as data (Task 24c union) split into TWO titled chip-lists —
  // alternate codes and alternate names — instead of comma blobs.
  function aliasListHTML(title, values) {
    if (!values.length) return '';
    return '<li class="osr-alias-detail">'
      + '<span class="osr-alias-detail-title">' + title + '</span>'
      + '<ul class="osr-alias-list">' + values.map(function (v) { return '<li>' + escapeHtml(v) + '</li>'; }).join('') + '</ul>'
      + '</li>';
  }
  function colorInfoDrawerHTML(color, index, surface) {
    var codes = aliasCodes(color || {});
    var names = aliasNames(color || {});
    // No alternate codes/names = no additional info beyond the code+year already on the
    // card face → return null so renderResultsHTML omits the icon entirely (owner wave 3.1).
    if (!codes.length && !names.length) return null;
    var panelId = 'osr-inf-' + safeIdPart(surface) + '-' + index;
    var items = aliasListHTML('Alternate codes', codes) + aliasListHTML('Alternate names', names);
    return { id: panelId, html: drawerPanelHTML(panelId, 'Also known as', items) };
  }

  // Task 20 (owner wave 2, #2): US market doesn't sell by liter — hidden per
  // owner 2026-07-15. Function kept (name + signature unchanged, still called
  // from the card foot below) so per-liter pricing can come back with a
  // one-line revert if a future market needs it.
  function priceLineHTML(color) {
    return '';
  }

  // Task 20 (owner wave 2, #1): finish-type info dialog copy — verbatim owner
  // copy, one entry per finish deriveFinish() can produce. Shared by the
  // dialog markup below AND the node test (exported as FINISH_INFO).
  var FINISH_INFO = [
    { key: 'Solid', text: 'One uniform color with no sparkle — the classic factory finish.' },
    { key: 'Metallic', text: 'Fine aluminum flakes add a bright sparkle that shifts with the light.' },
    { key: 'Pearl', text: 'Mica pigments give a soft shimmer and a subtle color shift at different angles.' },
    { key: 'Xirallic', text: 'Crystal-effect pigment with an intense, ultra-fine sparkle — stronger than standard metallic.' },
    { key: 'Candy', text: 'A translucent tinted layer sprayed over a base for extreme depth and gloss.' },
    { key: 'Special', text: 'Special-effect finishes like color-shift and chameleon — unique factory effects.' }
  ];

  // One shared dialog instance per <oem-search-results> (not per card) —
  // rendered once alongside the results grid; opened/highlighted per-card by
  // OEMSearchResults._openFinishDialog below (reads data-finish-item to know
  // which entry to mark as the current card's finish). role="dialog" +
  // aria-modal so a screen reader announces it as a modal; focus moves into
  // it on open (see _openFinishDialog) and back to the triggering chip on
  // close (_closeFinishDialog) per the a11y brief.
  function finishInfoDialogHTML() {
    var items = FINISH_INFO.map(function (f) {
      return '<li class="osr-finish-dialog-item" data-finish-item="' + f.key + '">'
        + '<span class="osr-finish-dialog-name">' + escapeHtml(f.key) + '</span>'
        + '<span class="osr-finish-dialog-desc">' + escapeHtml(f.text) + '</span>'
        + '</li>';
    }).join('');
    return '<div class="osr-finish-dialog-overlay" data-finish-dialog-overlay hidden>'
      +   '<div class="osr-finish-dialog" role="dialog" aria-modal="true" aria-labelledby="osr-finish-dialog-title" tabindex="-1">'
      +     '<div class="osr-finish-dialog-head">'
      +       '<h2 class="osr-finish-dialog-title" id="osr-finish-dialog-title">Paint finish types</h2>'
      +       '<button type="button" class="osr-finish-dialog-close" aria-label="Close">'
      +         '<i data-lucide="x"></i>'
      +       '</button>'
      +     '</div>'
      +     '<ul class="osr-finish-dialog-list">' + items + '</ul>'
      +   '</div>'
      + '</div>';
  }

  function renderResultsHTML(colors, opts) {
    opts = opts || {};
    var surface = opts.surface || 'search_page';
    var hlCode = opts.highlight && opts.highlight.code
      ? String(opts.highlight.code).toUpperCase().replace(/[^A-Z0-9]/g, '') : '';
    return colors.map(function (c, i) {
      var finish = c.finish || 'Solid';
      var cardFinishClass = (finish === 'Metallic' || finish === 'Xirallic' || finish === 'Special') ? ' is-metallic'
        : (finish === 'Pearl' ? ' is-pearl' : '');
      var isMatch = hlCode && String(c.colorCode || '').toUpperCase().replace(/[^A-Z0-9]/g, '') === hlCode;
      var name = c.colorName;
      // Owner wave 3: two drawer icon buttons (compat + colour identity) on the
      // left of the foot, "Choose Your Size" CTA button on the right. The icons
      // carry .osr-vehicles-more so the existing toggle wiring drives them;
      // data-drawer feeds the analytics metadata in _toggleVehicles.
      var veh = vehiclesDrawerHTML(c.vehicles, i, surface);
      var inf = colorInfoDrawerHTML(c, i, surface); // null when the colour has no alt codes/names
      var icoBtn = function (drawer, id, icon, label) {
        return '<button type="button" class="osr-vehicles-more osr-ico" data-drawer="' + drawer + '" data-color-index="' + i
          + '" aria-expanded="false" aria-controls="' + id + '" aria-label="' + escapeHtml(label) + '" title="' + escapeHtml(label) + '">'
          + '<i data-lucide="' + icon + '"></i></button>';
      };
      var icons = (veh ? icoBtn('vehicles', veh.id, 'car-front', 'Compatible vehicles') : '')
        + (inf ? icoBtn('info', inf.id, 'tags', 'Also known as') : '');
      var foot = (icons ? '<div class="osr-ico-group">' + icons + '</div>' : '')
        + '<a href="#" class="osr-cta">Choose Your Size <i data-lucide="arrow-right"></i></a>';
      // Task 20 (owner wave 2, #1 — swatch-chip fix pass): the top-left
      // finish tag ON THE SWATCH is the ONE-AND-ONLY finish indicator AND
      // the dialog trigger (wave 3 moved code/years off the face entirely —
      // they live in the tag-icon drawer now). The swatch tag
      // became a real <button> (was a <span>): data-finish-chip is the same
      // click/keydown-handler hook as before (the handlers select on the
      // attribute, so they carried over untouched), data-color-index feeds
      // _openFinishDialog's highlight, aria-haspopup announces the dialog.
      // It sits INSIDE the card's click target — the existing
      // [data-finish-chip] guards are what keep a tap on it from
      // navigating; the swatch AROUND it still navigates. Tap-target size
      // is handled in CSS (::after hit-area expansion to ~44px).
      return '<article class="osr-card' + cardFinishClass + (isMatch ? ' osr-card--match' : '') + '" data-color-index="' + i
          + '" tabindex="0" role="link" aria-label="' + escapeHtml(name) + '">'
        + '<div class="osr-swatch" style="' + swatchStyle(c) + '">'
        +   '<button type="button" class="osr-finish-tag" data-finish="' + escapeHtml(finish)
        +     '" data-finish-chip data-color-index="' + i + '" aria-haspopup="dialog" aria-label="'
        +     escapeHtml(finish) + ' finish. What does this mean?"><i data-lucide="'
        +     (FINISH_ICON[finish] || 'square') + '"></i>' + escapeHtml(finish) + '</button>'
        +   (isMatch ? '<span class="osr-badge--match">Your match</span>' : '')
        + '</div>'
        + '<div class="osr-body">'
        +   '<div class="osr-brandrow"><div class="osr-brand">' + escapeHtml(c.brandName) + '</div>'
        +     '<span class="osr-code">' + escapeHtml(c.colorCode)
        +       (yearRangeText(c) ? ' <span class="osr-code-yr">· ' + escapeHtml(yearRangeText(c)) + '</span>' : '') + '</span></div>'
        +   '<h3 class="osr-name">' + escapeHtml(name) + '</h3>'
        +   '<div class="osr-foot">' + foot + '</div>'
        +   (veh ? veh.html : '') + (inf ? inf.html : '')
        + '</div></article>';
    }).join('');
  }

  // Vehicle confirmation card ("we found your vehicle"). Rich when Partslink
  // resolved an exact colour; basic (identity only) otherwise. Pure → testable.
  function vehicleYear(fields) {
    var d = (fields && (fields['Date of production'] || fields['Production date'])) || '';
    var m = String(d).match(/(19|20)\d{2}/);
    return m ? m[0] : '';
  }
  function specChip(fields, label) {
    var v = fields && fields[label];
    return v ? '<span class="osv-chip">' + escapeHtml(v) + '</span>' : '';
  }
  function renderVehicleCard(data) {
    data = data || {};
    var ps = data.partslink;
    if (ps && ps.vehicle) {
      var f = ps.vehicle.fields || {};
      var title = (ps.vehicle.brand ? ps.vehicle.brand + ' ' : '')
        + (f['Market specification designation'] || f['Model Designation'] || data.model || '');
      var yr = vehicleYear(f);
      var rgb = (ps.best && ps.best.rgb_hex) || '#888';
      var chips = [specChip(f,'Body'), specChip(f,'Engine Code'), specChip(f,'Doors'), specChip(f,'Drive')].join('');
      return '<div class="osv-card osv-card--rich">'
        + '<div class="osv-found"><i data-lucide="check-circle-2"></i><span>We found your vehicle</span></div>'
        + '<div class="osv-main">'
        +   '<div class="osv-swatch" style="background:' + escapeHtml(rgb) + '">'
        +     '<span class="osv-badge">Your match</span></div>'
        +   '<div class="osv-info">'
        +     '<div class="osv-title">' + escapeHtml(title.trim()) + (yr ? ' · ' + yr : '') + '</div>'
        +     '<div class="osv-colour">' + escapeHtml(ps.vehicle.colourName || '')
        +       (ps.vehicle.colourCode ? ' <span class="osv-code">#' + escapeHtml(ps.vehicle.colourCode) + '</span>' : '') + '</div>'
        +     (chips ? '<div class="osv-chips">' + chips + '</div>' : '')
        +   '</div>'
        + '</div></div>';
    }
    var mk = [data.year, data.make, data.model].filter(Boolean).join(' ');
    // OEM palette — colours THIS trim was offered in (VehicleDatabases). Honestly a
    // picker, not the car's exact paint: "tap yours". Each swatch runs a colour search.
    var cols = data.colors || [];
    var palette = '';
    if (cols.length) {
      var sw = cols.map(function (c) {
        var label = c.name || c.code || '';
        return '<button type="button" class="osv-pal-sw" data-pal-code="' + escapeHtml(c.code || '') + '" data-pal-name="' + escapeHtml(c.name || '') + '" aria-label="' + escapeHtml(label) + '">'
          + '<span class="osv-pal-chip" style="background:' + escapeHtml(c.hex || '#cccccc') + '"></span>'
          + '<span class="osv-pal-label">' + escapeHtml(label) + (c.code ? ' <span class="osv-pal-code">' + escapeHtml(c.code) + '</span>' : '') + '</span>'
          + '</button>';
      }).join('');
      palette = '<div class="osv-palette"><div class="osv-palette-head">Colours your '
        + escapeHtml(mk) + ' came in — tap yours to find the paint</div>'
        + '<div class="osv-palette-grid">' + sw + '</div></div>';
    }
    return '<div class="osv-card osv-card--basic">'
      + '<div class="osv-found"><i data-lucide="check-circle-2"></i><span>We found your vehicle</span></div>'
      + '<div class="osv-title">' + escapeHtml(mk) + '</div>'
      + (palette || '<div class="osv-sub">Showing colours available for your ' + escapeHtml(mk) + '</div>')
      + '</div>';
  }

  function renderStateHTML(state, opts) {
    opts = opts || {};
    if (state === 'loading') {
      var one = '<div class="osr-skel"><div class="osr-skel-swatch"></div><div class="osr-skel-body">'
        + '<div class="osr-skel-line w40"></div><div class="osr-skel-line w70"></div>'
        + '<div class="osr-skel-line w90"></div><div class="osr-skel-line w55"></div></div></div>';
      var out = '';
      for (var i = 0; i < 6; i++) out += one;
      return out;
    }
    if (state === 'empty') {
      return '<div class="osr-state" data-osr="empty">'
        + '<div class="osr-state-icon"><i data-lucide="search-x"></i></div>'
        + '<h2 class="osr-state-title">No colors found</h2>'
        + '<p class="osr-state-text">No colors found — try the paint code from your door jamb sticker.</p>'
        + '</div>';
    }
    return '<div class="osr-state" data-osr="error">'
      + '<div class="osr-state-icon"><i data-lucide="triangle-alert"></i></div>'
      + '<h2 class="osr-state-title">Something went wrong</h2>'
      + '<p class="osr-state-text">We couldn’t reach the color library. Check your connection and try again — or search by the paint code on your door jamb sticker. Still stuck? <a href="/pages/contact-us">Contact us</a> and we’ll find your color.</p>'
      + '<button type="button" class="osr-retry"><i data-lucide="refresh-cw"></i><span>Retry</span></button>'
      + '</div>';
  }

  /* ── <oem-search-results> custom element (browser-only) ──────────── */
  if (isBrowser && typeof customElements !== 'undefined' && !customElements.get('oem-search-results')) {
    var OEMSearchResults = function () { return Reflect.construct(HTMLElement, [], OEMSearchResults); };
    OEMSearchResults.prototype = Object.create(HTMLElement.prototype);
    OEMSearchResults.prototype.constructor = OEMSearchResults;
    Object.setPrototypeOf(OEMSearchResults, HTMLElement);

    OEMSearchResults.prototype.connectedCallback = function () {
      this._colors = [];
      this.classList.add('osr-root');
      var self = this;

      this.addEventListener('click', function (e) {
        var retryBtn = e.target.closest('.osr-retry');
        if (retryBtn) { if (self._retryFn) self._retryFn(); return; }

        // Expand-in-place vehicle compatibility (owner sign-off 2026-07-14;
        // Task 20 turned the panel into an overlay with a close "x" button):
        // opening, closing, and clicks inside the panel itself (e.g. reading
        // a model/year row) must NOT navigate, so all three are handled +
        // returned before the card-click branch below ever runs. The panel
        // now visually covers the whole card while open, so every click
        // inside the card's bounds lands on the panel or a descendant —
        // that's what the closest('.osr-vehicles-panel') guard below is
        // actually blocking now, not just "clicks on list rows".
        var toggleBtn = e.target.closest('.osr-vehicles-more');
        if (toggleBtn) { self._toggleVehicles(toggleBtn); return; }
        var closeBtn = e.target.closest('.osr-vehicles-close');
        if (closeBtn) {
          var toggleForClose = self._toggleForPanel(closeBtn.closest('.osr-vehicles-panel'));
          if (toggleForClose) self._toggleVehicles(toggleForClose, false);
          return;
        }
        if (e.target.closest('.osr-vehicles-panel')) return;

        // Task 20 (owner wave 2, #1): finish-type info dialog. The chip
        // itself is the trigger button (data-finish-chip); the close button
        // and a backdrop click both dismiss it. All three are handled +
        // returned here, same guard pattern as the vehicles panel above, so
        // none of them ever falls through to card-open navigation.
        var finishChip = e.target.closest('[data-finish-chip]');
        if (finishChip) { self._openFinishDialog(finishChip); return; }
        var finishClose = e.target.closest('.osr-finish-dialog-close');
        if (finishClose) { self._closeFinishDialog(); return; }
        var finishOverlay = e.target.closest('[data-finish-dialog-overlay]');
        if (finishOverlay && !e.target.closest('.osr-finish-dialog')) { self._closeFinishDialog(); return; } // backdrop click
        if (e.target.closest('.osr-finish-dialog')) return; // clicks inside the dialog body (e.g. reading an entry)

        // .osr-cta is an href="#" anchor (Task 8 owns its :focus-visible
        // styling) — prevent the hash-jump, then fall through to the same
        // card-open navigation as a body click.
        var cta = e.target.closest('.osr-cta');
        if (cta) e.preventDefault();

        var card = e.target.closest('.osr-card');
        if (card) self._openCard(parseInt(card.getAttribute('data-color-index'), 10));
      });

      this.addEventListener('keydown', function (e) {
        // Escape closes the finish-info dialog (if open) or an open vehicles
        // panel, and returns focus to whatever triggered it. Dialog checked
        // first since it's a page-level overlay outside any one card — its
        // own closest('.osr-vehicles-panel') would never match.
        if (e.key === 'Escape') {
          var openDialog = self.querySelector('[data-finish-dialog-overlay]:not([hidden])');
          if (openDialog) { self._closeFinishDialog(); e.stopPropagation(); return; }
          var openPanel = e.target.closest('.osr-vehicles-panel');
          if (openPanel && !openPanel.hidden) {
            var toggleForEsc = self._toggleForPanel(openPanel);
            if (toggleForEsc) { self._toggleVehicles(toggleForEsc, false); e.stopPropagation(); return; }
          }
        }
        // Real <button> elements already turn Enter/Space into a click, which
        // the listener above handles — bail here so we don't ALSO navigate
        // the enclosing card (e.target.closest('.osr-card') would still match
        // since the button lives inside the card).
        if (e.target.closest('.osr-vehicles-more')) return;
        if (e.target.closest('.osr-vehicles-panel')) return;
        if (e.target.closest('[data-finish-chip]')) return;
        if (e.target.closest('.osr-finish-dialog') || e.target.closest('[data-finish-dialog-overlay]')) return;
        // Same double-fire hazard for the CTA anchor: Enter on a focused
        // <a href="#"> triggers the browser's NATIVE anchor activation — a
        // synthetic click that the click handler above already routes to
        // _openCard (with preventDefault on the hash-jump). Without this
        // bail, this keydown handler would ALSO call _openCard (the anchor
        // sits inside .osr-card, so closest() matches), double-firing
        // result_clicked + navigation. The click handler owns CTA
        // activation for both mouse and keyboard.
        if (e.target.closest('.osr-cta')) return;
        if (e.key !== 'Enter') return;
        var card = e.target.closest('.osr-card');
        if (card) self._openCard(parseInt(card.getAttribute('data-color-index'), 10));
      });
    };

    OEMSearchResults.prototype._surface = function () { return this.getAttribute('surface') || 'search_page'; };
    OEMSearchResults.prototype._track = function (action, meta) {
      if (window.DYCAnalytics && typeof window.DYCAnalytics.track === 'function') {
        window.DYCAnalytics.track('custom', { category: 'oem_search', action: action, metadata: meta || {} });
      }
    };

    // Task 20: resolve a panel back to the "+N more" button that owns it
    // (aria-controls is the only link between them) — used by the close
    // button and Escape, neither of which has the toggle as e.target.
    OEMSearchResults.prototype._toggleForPanel = function (panel) {
      if (!panel || !panel.id) return null;
      return this.querySelector('.osr-vehicles-more[aria-controls="' + panel.id + '"]');
    };

    // `open`: force open/closed (close button, Escape); omitted (the "+N
    // more" toggle itself) flips current state. Also owns focus management —
    // required by the Task 20 overlay redesign: focus moves INTO the panel
    // (its close button) on open, and back to the toggle on close, so
    // keyboard users never lose their place when the panel covers the card.
    OEMSearchResults.prototype._toggleVehicles = function (btn, open) {
      var panelId = btn.getAttribute('aria-controls');
      var panel = panelId ? this.querySelector('#' + panelId) : null;
      var wasExpanded = btn.getAttribute('aria-expanded') === 'true';
      var nextOpen = (typeof open === 'boolean') ? open : !wasExpanded;
      if (nextOpen === wasExpanded) return;
      btn.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
      if (panel) panel.hidden = !nextOpen;
      if (nextOpen) {
        // Accordion: a card can have two drawers (vehicles + also-known-as). Only one open
        // at a time, so opening one closes its sibling — the panel is a full-card overlay,
        // so two open at once would stack and read as "the icon didn't work".
        var card = btn.closest('.osr-card');
        if (card) {
          var others = card.querySelectorAll('.osr-vehicles-more[aria-expanded="true"]');
          for (var o = 0; o < others.length; o++) {
            if (others[o] === btn) continue;
            others[o].setAttribute('aria-expanded', 'false');
            var oid = others[o].getAttribute('aria-controls');
            var op = oid ? this.querySelector('#' + oid) : null;
            if (op) op.hidden = true;
          }
        }
        var index = parseInt(btn.getAttribute('data-color-index'), 10);
        var color = this._colors[index];
        if (color) this._track('compat_expanded', { color_code: color.colorCode, surface: this._surface(), drawer: btn.getAttribute('data-drawer') || 'vehicles' });
        var closeBtn = panel ? panel.querySelector('.osr-vehicles-close') : null;
        if (closeBtn && typeof closeBtn.focus === 'function') closeBtn.focus();
      } else if (typeof btn.focus === 'function') {
        btn.focus();
      }
    };

    // Task 20 (owner wave 2, #1): open the shared finish-info dialog, highlight
    // the triggering card's own finish type, move focus INTO the dialog (its
    // close button — same focus-management pattern as _toggleVehicles above),
    // and remember the trigger chip so _closeFinishDialog can return focus.
    OEMSearchResults.prototype._openFinishDialog = function (chip) {
      var overlay = this.querySelector('[data-finish-dialog-overlay]');
      if (!overlay) return;
      var index = parseInt(chip.getAttribute('data-color-index'), 10);
      var color = this._colors[index];
      var finish = (color && color.finish) || 'Solid';
      var items = overlay.querySelectorAll('[data-finish-item]');
      for (var i = 0; i < items.length; i++) {
        var isCurrent = items[i].getAttribute('data-finish-item') === finish;
        items[i].classList.toggle('is-current', isCurrent);
        if (isCurrent) items[i].setAttribute('aria-current', 'true');
        else items[i].removeAttribute('aria-current');
      }
      overlay.hidden = false;
      this._finishDialogTrigger = chip;
      var dialog = overlay.querySelector('.osr-finish-dialog');
      if (dialog && typeof dialog.focus === 'function') dialog.focus();
      this._track('finish_info_opened', { finish: finish, surface: this._surface() });
    };

    OEMSearchResults.prototype._closeFinishDialog = function () {
      var overlay = this.querySelector('[data-finish-dialog-overlay]');
      if (!overlay || overlay.hidden) return;
      overlay.hidden = true;
      var trigger = this._finishDialogTrigger;
      this._finishDialogTrigger = null;
      if (trigger && typeof trigger.focus === 'function') trigger.focus();
    };

    OEMSearchResults.prototype._openCard = function (index) {
      var color = this._colors[index];
      if (!color) return;
      this._track('result_clicked', {
        position: index, color_code: color.colorCode,
        has_gid: !!(color.enrichment && color.enrichment.shopifyProductGid),
        surface: this._surface()
      });
      resolveProductUrl(color, { surface: this._surface() }).then(function (url) {
        window.location.href = url;
      });
    };

    // Task 8 touch-up: every render path below injects fresh `data-lucide`
    // placeholders (skeleton/empty/error icons, and the results grid's
    // per-card + bar/load-more icons), so each one must re-trigger the
    // guarded materialization itself — <oem-search-app>._lucide() only fires
    // once per _run() and misses this element's own onLoadMore re-renders
    // (flagged by Task 7's reviewer) plus any standalone use of
    // <oem-search-results> outside <oem-search-app>.
    OEMSearchResults.prototype._lucide = function () {
      if (window.lucide && typeof window.lucide.createIcons === 'function') {
        try { window.lucide.createIcons(); } catch (e) { /* icon lib optional */ }
      }
    };

    // Bubbling state beacon: <oem-search-app> listens to show the finish filter
    // chips only while actual results are on screen (loading/error/empty → 0).
    OEMSearchResults.prototype._announce = function (count) {
      try { this.dispatchEvent(new CustomEvent('oem-results-state', { bubbles: true, detail: { count: count } })); } catch (e) { /* CustomEvent optional */ }
    };
    OEMSearchResults.prototype.showLoading = function () {
      this.innerHTML = renderStateHTML('loading', {});
      this._lucide();
      this._announce(0);
    };
    OEMSearchResults.prototype.clear = function () { this.innerHTML = ''; this._colors = []; this._announce(0); };
    OEMSearchResults.prototype.showError = function (retryFn) {
      this._retryFn = retryFn || null;
      this.innerHTML = renderStateHTML('error', {});
      this._lucide();
      this._announce(0);
    };
    OEMSearchResults.prototype.setResults = function (colors, opts) {
      opts = opts || {};
      this._colors = colors;
      this._announce(colors.length);
      if (!colors.length) {
        this.innerHTML = renderStateHTML('empty', {});
        this._lucide();
        this._track('results_empty', { surface: this._surface() });
        return;
      }
      var total = opts.total != null ? opts.total : colors.length;
      var html = '<div class="osr-bar"><div class="osr-count">Showing <b>' + colors.length + '</b> of ' + total + ' colors</div>'
        + '<div class="osr-sort"><i data-lucide="arrow-down-wide-narrow"></i> Sorted by best match</div></div>'
        + '<div class="osr-grid">' + renderResultsHTML(colors, { surface: this._surface(), highlight: opts.highlight }) + '</div>';
      if (total > colors.length && opts.onLoadMore) {
        html += '<div class="osr-loadmore"><button type="button" class="osr-loadmore-btn"><i data-lucide="plus"></i><span>Load more colors</span></button></div>';
      }
      // Task 20 (owner wave 2, #1): ONE shared finish-info dialog per
      // <oem-search-results>, appended alongside the grid (not one per
      // card) — see finishInfoDialogHTML() above.
      html += finishInfoDialogHTML();
      this.innerHTML = html;
      this._lucide();
      var lm = this.querySelector('.osr-loadmore-btn');
      if (lm && opts.onLoadMore) lm.addEventListener('click', function () { opts.onLoadMore(); });
      this._track('results_viewed', { count: colors.length, total: total, surface: this._surface() });
    };

    customElements.define('oem-search-results', OEMSearchResults);
  }

  /* ── Shared search driver + native routing ───────────────────────── */
  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments, self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, ms);
    };
  }

  function nativeSearchUrl(params) {
    params = params || {};
    var tool = params.tool || (params.brandId ? 'vehicle' : 'color');
    return buildSearchPageUrl(config().searchPageUrl, {
      tool: tool, q: params.q || '',
      brandId: params.brandId ? String(params.brandId) : '',
      modelId: params.modelId ? String(params.modelId) : '',
      year: params.year ? String(params.year) : '',
      make: params.make || '', model: params.model || ''
    });
  }

  // Drawer hook: nav-finder sets window.__oemDrawerSearch = fn(params) when its
  // drawer is open; openNative prefers it so header searches stay in-context.
  function openNative(params) {
    if (isBrowser && typeof window.__oemDrawerSearch === 'function') {
      window.__oemDrawerSearch(params);
      return;
    }
    window.location.href = nativeSearchUrl(params);
  }

  // Task 24b, RETAINED under 24c as belt-and-braces: drop rows the server
  // ITSELF flags `is_variant: true` (shade variants — "BRILLANTSILBER M. 2"
  // etc., recipe tweaks of one paint, not distinct colours). The grouping
  // contract (docs/shopify-grouping-and-variants-explainer.md) says the
  // browse list (group=true) hides variants server-side, but the LIVE API
  // still returns them (probed 2026-07-17: q=744 and q=brillantsilber with
  // explicit group=true each ship 5 is_variant:true rows) — so this thin
  // respect-the-server-flag pass stays. It is SELF-NEUTRALIZING: the day the
  // server actually excludes variants from grouped results, kept === input,
  // removed is 0, and display totals are exactly the server's — delete this
  // then, or leave it as a harmless identity. Absent flag == false (the
  // doc's own semantics). Lives in the CARD driver, not searchColors —
  // oem-pdp-blocks.js needs full record fidelity for its exact-code lookup.
  // Safety net: if EVERY fetched row is a variant, keep them all — mixing-
  // recipe rows beat an empty results page. Pure → node-testable; never
  // mutates its input.
  function filterVariantRows(colors) {
    colors = colors || [];
    var kept = colors.filter(function (c) { return !c.isVariant; });
    return kept.length ? kept : colors.slice();
  }

  // Stable re-rank for the VIN flow (opt-in via params.prioritize): float the
  // customer's exact factory colour code to the very top — and among same-code
  // records, the one whose name matches the OEM's (e.g. "Portimao Blue" over the
  // mis-labelled "Grayish Brown" C31 twin) — keeping the rest as "related".
  // Only runs when a prioritize hint is present, so other tools are untouched.
  function prioritizeColors(colors, pr) {
    var norm = function (s) { return String(s == null ? '' : s).toUpperCase().replace(/[^A-Z0-9]/g, ''); };
    var codeN = norm(pr.code), tokN = norm(pr.nameToken || '');
    function score(c) {
      var s = 0;
      if (codeN && norm(c.colorCode) === codeN) s += 4; // exact factory code
      if (tokN && norm(c.colorName).indexOf(tokN) !== -1) s += 2; // OEM name match
      return s;
    }
    return colors
      .map(function (c, i) { return { c: c, i: i, s: score(c) }; })
      .sort(function (a, b) { return b.s - a.s || a.i - b.i; }) // stable: keep V3 order within a tier
      .map(function (x) { return x.c; });
  }

  // Shared results driver: nav-finder (Task 10) and <oem-search-app> both use it.
  // state = { seq: 0, page: 1, colors: [] } owned by the caller.
  function runSearchInto(resultsEl, params, state) {
    var seq = ++state.seq;
    if (params.page === 1 || !params.page) { state.page = 1; state.colors = []; resultsEl.showLoading(); }
    var t0 = Date.now();
    return searchColors({
      q: params.q, brandId: params.brandId, modelId: params.modelId, year: params.year,
      onlyMetallic: params.onlyMetallic, onlyPearl: params.onlyPearl,
      page: state.page, pageSize: params.pageSize,
      // Task 24c: card surfaces are the browse list — one card per paint,
      // alias union on the rep. Explicit (it's also the server default) so
      // the request self-documents against the grouping contract.
      group: true
    }).then(function (res) {
      if (seq !== state.seq) return res; // superseded
      state.colors = state.colors.concat(res.colors);
      // Task 24b: card surfaces hide shade-variant rows. Filter the DISPLAY
      // copy only — state.colors stays the raw fetch-order accumulation so
      // server page math (page N against total_hits) is untouched. `removed`
      // counts variants seen so far; the advertised total is res.total minus
      // those, so the count bar never promises rows that will be filtered.
      // Unfetched pages may still hide more variants, so the "of N" can
      // shrink as pages load — that's the honest direction: it never
      // undercounts what's shown, converges to displayed == total once every
      // page is fetched, and the load-more button's visibility condition
      // (total > shown) stays exactly equivalent to "more server pages
      // exist" (res.total > state.colors.length). When the safety net kept
      // an all-variant set, removed is 0 and the total is the server's.
      var display = filterVariantRows(state.colors);
      var removed = state.colors.length - display.length;
      // Keep state.colors in fetch order (for load-more concat); display a
      // prioritized copy when the VIN flow asked for it.
      var ordered = params.prioritize ? prioritizeColors(display.slice(), params.prioritize) : display;
      resultsEl.setResults(ordered, {
        total: Math.max(display.length, res.total - removed),
        highlight: params.prioritize,
        onLoadMore: function () { state.page++; runSearchInto(resultsEl, Object.assign({}, params, { page: state.page }), state); }
      });
      if (window.DYCAnalytics && typeof window.DYCAnalytics.track === 'function') {
        window.DYCAnalytics.track('custom', { category: 'oem_search', action: 'search_executed',
          metadata: { tool: params.tool || (params.brandId ? 'vehicle' : 'color'),
            latency_ms: Date.now() - t0, total: res.total, surface: resultsEl.getAttribute('surface') } });
      }
      return res;
    }).catch(function (err) {
      if (seq !== state.seq) return;
      resultsEl.showError(function () { runSearchInto(resultsEl, params, state); });
      console.error('[oem-search] search failed', err);
    });
  }

  /* ── <oem-search-app> custom element (browser-only) ───────────────── */
  // Full search experience. Shell markup + osa-* classes are lifted from the
  // owner-approved visual prototype (templates/oem-search-prototype.html) — the
  // dev-harness bits (proto-bar, hardcoded HITS, fake latency) are NOT lifted.
  // Behaviour = the 8-point contract in the Task 7 brief.
  var OSA_US_STATES = [
    { c: 'AL', n: 'Alabama' }, { c: 'AK', n: 'Alaska' }, { c: 'AZ', n: 'Arizona' },
    { c: 'AR', n: 'Arkansas' }, { c: 'CA', n: 'California' }, { c: 'CO', n: 'Colorado' },
    { c: 'CT', n: 'Connecticut' }, { c: 'DE', n: 'Delaware' }, { c: 'FL', n: 'Florida' },
    { c: 'GA', n: 'Georgia' }, { c: 'HI', n: 'Hawaii' }, { c: 'ID', n: 'Idaho' },
    { c: 'IL', n: 'Illinois' }, { c: 'IN', n: 'Indiana' }, { c: 'IA', n: 'Iowa' },
    { c: 'KS', n: 'Kansas' }, { c: 'KY', n: 'Kentucky' }, { c: 'LA', n: 'Louisiana' },
    { c: 'ME', n: 'Maine' }, { c: 'MD', n: 'Maryland' }, { c: 'MA', n: 'Massachusetts' },
    { c: 'MI', n: 'Michigan' }, { c: 'MN', n: 'Minnesota' }, { c: 'MS', n: 'Mississippi' },
    { c: 'MO', n: 'Missouri' }, { c: 'MT', n: 'Montana' }, { c: 'NE', n: 'Nebraska' },
    { c: 'NV', n: 'Nevada' }, { c: 'NH', n: 'New Hampshire' }, { c: 'NJ', n: 'New Jersey' },
    { c: 'NM', n: 'New Mexico' }, { c: 'NY', n: 'New York' }, { c: 'NC', n: 'North Carolina' },
    { c: 'ND', n: 'North Dakota' }, { c: 'OH', n: 'Ohio' }, { c: 'OK', n: 'Oklahoma' },
    { c: 'OR', n: 'Oregon' }, { c: 'PA', n: 'Pennsylvania' }, { c: 'RI', n: 'Rhode Island' },
    { c: 'SC', n: 'South Carolina' }, { c: 'SD', n: 'South Dakota' }, { c: 'TN', n: 'Tennessee' },
    { c: 'TX', n: 'Texas' }, { c: 'UT', n: 'Utah' }, { c: 'VT', n: 'Vermont' },
    { c: 'VA', n: 'Virginia' }, { c: 'WA', n: 'Washington' }, { c: 'WV', n: 'West Virginia' },
    { c: 'WI', n: 'Wisconsin' }, { c: 'WY', n: 'Wyoming' }, { c: 'DC', n: 'District of Columbia' }
  ];
  var OSA_INSTANCE = 0;

  if (isBrowser && typeof customElements !== 'undefined' && !customElements.get('oem-search-app')) {
    var OEMSearchApp = function () { return Reflect.construct(HTMLElement, [], OEMSearchApp); };
    OEMSearchApp.prototype = Object.create(HTMLElement.prototype);
    OEMSearchApp.prototype.constructor = OEMSearchApp;
    Object.setPrototypeOf(OEMSearchApp, HTMLElement);

    /* ── lifecycle ──────────────────────────────────────────────────── */
    OEMSearchApp.prototype.connectedCallback = function () {
      if (this._mounted) return;
      this._mounted = true;
      this.classList.add('osa-root'); // Task 8 CSS scope root (mirrors OEMSearchResults' osr-root)
      this._uid = 'osa-' + (++OSA_INSTANCE);
      this._surface = this.getAttribute('surface') || 'search_page';
      this._syncUrl = this.getAttribute('data-sync-url') === 'true';
      this._defaultTool = this.getAttribute('data-default-tool') || 'vehicle';
      // Task 20 (owner wave 2, #4): license-plate kill switch. Read once at
      // mount (theme setting, doesn't change mid-session).
      this._plateEnabled = config().plateEnabled;
      if (this._defaultTool === 'plate' && !this._plateEnabled) this._defaultTool = 'vehicle';

      // Per-placement tool visibility. `data-tools` is a comma list; absent means
      // show everything, so existing placements are unaffected. This is separate
      // from OEMSearchConfig.plateEnabled, which is the global kill switch — a
      // section can hide a tool the site still allows.
      var toolAttr = (this.getAttribute('data-tools') || '').trim();
      this._tools = toolAttr
        ? toolAttr.split(',').map(function (s) { return s.trim(); }).filter(Boolean)
        : ['vehicle', 'color', 'car', 'locate'];
      // The locate tab needs server-rendered content to show; without it there is
      // nothing to display, so drop it rather than render an empty panel.
      this._locateSrc = this.getAttribute('data-locate-source') || '';
      if (this._tools.indexOf('locate') > -1 && !this._locateSrc) {
        this._tools = this._tools.filter(function (t) { return t !== 'locate'; });
      }
      if (this._tools.indexOf(this._defaultTool) < 0) this._defaultTool = this._tools[0] || 'vehicle';
      // Task 20 (owner wave 2, #7b): homepage-ready results mode. "navigate"
      // sends searches to the dedicated search page instead of rendering
      // inline (see _run below); anything else (including the attribute
      // being absent) is the existing inline behavior.
      this._resultsMode = this.getAttribute('data-results-mode') === 'navigate' ? 'navigate' : 'inline';
      this._state = { seq: 0, page: 1, colors: [] };      // owned; passed to runSearchInto
      this._filters = { onlyMetallic: false, onlyPearl: false };
      this._lastParams = null;                             // base params (sans filters) for re-run
      this._lastAppliedSearch = null;                      // popstate loop guard
      this._makesPromise = null;

      this._render();
      this._wire();
      this._lucide();

      // (7) Restore tool + inputs from the URL whenever params exist. Reading is
      // safe on every placement — the mega menu deep-links the HOMEPAGE console
      // as /?tool=locate#oem-find-your-color, and that placement has sync OFF.
      // _syncUrl keeps gating WRITES only (see _pushUrl), so a non-sync page
      // never rewrites the address bar; it just honours the tool it was asked
      // to open on.
      var restored = false;
      {
        var p = paramsFromSearch(window.location.search);
        if (p.tool || p.q || p.brandId || p.make) { restored = true; this._restoreFromParams(p); }
      }

      // Size intent. The "2oz Touch-Up Paint" menu item lands here as
      // /?tool=vehicle&size=2oz — the shopper's mission is a touch-up, but the
      // product they'll finally reach is several navigations away (search →
      // results → colour PDP). Stash the intent for the session; the OEM PDP
      // size selector (snippets/oem-mcc-variant-selector.liquid) reads it and
      // preselects the matching size. sessionStorage so the mission dies with
      // the tab, and a manual size click on any PDP simply overrides it there.
      try {
        var sizeIntent = new URLSearchParams(window.location.search).get('size');
        if (sizeIntent) sessionStorage.setItem('oem-size-intent', sizeIntent);
      } catch (e) { /* storage blocked — the customer just picks the size manually */ }

      // (7b) Brand-page seed. On a collection page data-sync-url is false, so the
      // URL carries nothing and a Ford page would open on "Select Make" — silly
      // when the page is already Ford. data-make / data-brand-id preselect the
      // make through the same restore path.
      //
      // seedOnly:true stops _restoreVehicle calling _run() at the end. Without it
      // every brand page would fire a full V3 search on load and render results
      // that duplicate the server-rendered block above the finder.
      if (!restored) {
        var seedMake = (this.getAttribute('data-make') || '').trim();
        var seedBrandId = (this.getAttribute('data-brand-id') || '').trim();
        if (seedMake || seedBrandId) {
          restored = true;
          this._selectTool('vehicle');
          this._restoreVehicle({ make: seedMake, brandId: seedBrandId }, { seedOnly: true });
        }
      }

      if (!restored) this._selectTool(this._defaultTool);

      // (8) popstate → re-read + re-run, guarded against loops.
      var self = this;
      this._onPopState = function () {
        var key = window.location.search || '';
        if (key === self._lastAppliedSearch) return;
        self._restoreFromParams(paramsFromSearch(window.location.search));
      };
      if (this._syncUrl) window.addEventListener('popstate', this._onPopState);
    };

    OEMSearchApp.prototype.disconnectedCallback = function () {
      if (this._onPopState) window.removeEventListener('popstate', this._onPopState);
    };

    /* ── (1) shell render ───────────────────────────────────────────── */
    OEMSearchApp.prototype._render = function () {
      var uid = this._uid;
      var plateEnabled = this._plateEnabled;
      var stateOpts = '';
      for (var i = 0; i < OSA_US_STATES.length; i++) {
        stateOpts += '<option value="' + OSA_US_STATES[i].c + '">' + escapeHtml(OSA_US_STATES[i].n) + '</option>';
      }
      // Task 20 (owner wave 2, #4): license-plate kill switch. When off, the plate
      // side of the Your-Car toggle isn't rendered at all (see the car panel below,
      // gated on plateEnabled) — nothing in the DOM for a merchant/dev to find.
      this.innerHTML =
        '<section class="osa-console" aria-label="Paint color finder">'
        + (function (tools, dflt) {
            // Tabs are emitted in a fixed order but only for enabled tools, so a
            // placement can run e.g. vehicle + locate only. `is-active` follows the
            // resolved default rather than being hard-coded to vehicle.
            var defs = [
              { id: 'vehicle', icon: 'car',       full: 'By Vehicle',           short: 'Vehicle' },
              { id: 'color',   icon: 'palette',   full: 'By Code or Color',     short: 'Code / Color' },
              { id: 'car',     icon: 'scan-line', full: 'VIN or License Plate', short: 'VIN / Plate' },
              { id: 'locate',  icon: 'map-pin',   full: 'Where’s My Code?',     short: 'Find code' }
            ];
            var out = '<div class="osa-tabs" role="tablist" data-osa="tabs">';
            for (var i = 0; i < defs.length; i++) {
              var d = defs[i];
              if (tools.indexOf(d.id) < 0) continue;
              var on = d.id === dflt;
              out += '<button type="button" class="osa-tab' + (on ? ' is-active' : '') + '" role="tab"'
                + ' aria-selected="' + (on ? 'true' : 'false') + '" data-tab="' + d.id + '">'
                + '<i data-lucide="' + d.icon + '"></i>'
                + '<span class="osa-tab-label-full">' + d.full + '</span>'
                + '<span class="osa-tab-label-short">' + d.short + '</span></button>';
            }
            return out + '</div>';
          })(this._tools, this._defaultTool)
        // vehicle (default)
        + '<div class="osa-panel is-active" role="tabpanel" data-panel="vehicle">'
        +   '<div class="osa-cascade">'
        +     '<div class="osa-field"><label class="osa-label" for="' + uid + '-make">Make</label>'
        +       '<select class="osa-select" id="' + uid + '-make" data-role="make"><option value="">Select Make</option></select></div>'
        +     '<div class="osa-field"><label class="osa-label" for="' + uid + '-model">Model</label>'
        +       '<select class="osa-select" id="' + uid + '-model" data-role="model" disabled><option value="">Select Model</option></select></div>'
        +     '<div class="osa-field"><label class="osa-label" for="' + uid + '-year">Year</label>'
        +       '<select class="osa-select" id="' + uid + '-year" data-role="year" disabled><option value="">Select Year</option></select></div>'
        +   '</div>'
        +   '<div class="osa-row-actions"><button type="button" class="osa-btn" data-role="vehicle-search" disabled>'
        +     '<i data-lucide="search"></i><span>Search colors</span></button></div>'
        + '</div>'
        // color / code
        + '<div class="osa-panel" role="tabpanel" data-panel="color" hidden>'
        +   '<div class="osa-field osa-search"><label class="osa-label" for="' + uid + '-color">Paint code or color name</label>'
        +     '<div class="osa-search"><i data-lucide="search" class="osa-search-icon"></i>'
        +       '<input class="osa-input" id="' + uid + '-color" data-role="q" type="text" placeholder="e.g. Alpine White, Rosso Corsa, or code 300" autocomplete="off"></div></div>'
        +   '<div class="osa-row-actions"><button type="button" class="osa-btn" data-role="color-search">'
        +     '<i data-lucide="search"></i><span>Search colors</span></button></div>'
        + '</div>'
        // Your Car — one tab, [Plate | VIN] toggle → /api/vehicle/decode → card + results.
        // Plate is the default US-friendly input; when the plate kill-switch is off the
        // toggle collapses to VIN-only (no plate row, VIN sub-row shown).
        + '<div class="osa-panel" role="tabpanel" data-panel="car" hidden>'
        +   (plateEnabled
              ? '<div class="osa-carsub" data-carsub="plate">'
                +   '<div class="osa-plate-row">'
                +     '<div class="osa-field"><label class="osa-label" for="' + uid + '-plate">Plate Number</label>'
                +       '<input class="osa-input osa-plate-input" id="' + uid + '-plate" data-role="plate" type="text" placeholder="ABC 1234" autocomplete="off"></div>'
                +     '<div class="osa-field"><label class="osa-label" for="' + uid + '-state">State</label>'
                +       '<select class="osa-select" id="' + uid + '-state" data-role="state"><option value="">State</option>' + stateOpts + '</select></div>'
                +   '</div>'
                + '</div>'
              : '')
        +   '<div class="osa-carsub' + (plateEnabled ? '' : ' is-active') + '" data-carsub="vin"' + (plateEnabled ? ' hidden' : '') + '>'
        +     '<div class="osa-field osa-search"><label class="osa-label" for="' + uid + '-vin">Vehicle Identification Number</label>'
        +       '<div class="osa-search"><i data-lucide="hash" class="osa-search-icon"></i>'
        +         '<input class="osa-input osa-vin-input" id="' + uid + '-vin" data-role="vin" type="text" placeholder="17 Digit VIN" autocomplete="off" maxlength="17" spellcheck="false" autocapitalize="characters"></div></div>'
        +   '</div>'
        +   '<div class="osa-row-actions"><button type="button" class="osa-btn" data-role="car-search"><i data-lucide="search"></i><span>Find my car</span></button></div>'
        + '</div>'
        // console foot: the [Plate|VIN] toggle lives here (car tab only); the finish
        // filter chips share the row but only appear once there are results to filter.
        + '<div class="osa-foot" data-osa="foot" hidden>'
        +   (plateEnabled
              ? '<div class="osa-cartoggle" role="tablist" aria-label="Identify by" data-role="car-toggle" hidden>'
                + '<button type="button" class="osa-cartoggle-btn is-active" role="tab" aria-selected="true" data-cartype="plate"><i data-lucide="scan-line"></i><span>License Plate</span></button>'
                + '<button type="button" class="osa-cartoggle-btn" role="tab" aria-selected="false" data-cartype="vin"><i data-lucide="hash"></i><span>VIN</span></button>'
                + '</div>'
              : '')
        +   '<button type="button" class="osa-scan-chip" data-role="car-scan" hidden><i data-lucide="camera"></i><span>' + (plateEnabled ? 'Scan plate' : 'Scan VIN') + '</span></button>'
        +   '<input type="file" accept="image/*" capture="environment" data-role="car-file" hidden>'
        +   '<div class="osa-filters" role="group" aria-label="Finish filters" data-role="finish-filters" hidden>'
        +     '<span class="osa-filters-label">Finish</span>'
        +     '<button type="button" class="osa-chip" data-filter="metallic" aria-pressed="false"><i data-lucide="sparkles"></i><span>Metallic</span></button>'
        +     '<button type="button" class="osa-chip" data-filter="pearl" aria-pressed="false"><i data-lucide="droplet"></i><span>Pearl</span></button>'
        +   '</div>'
        + '</div>'
        // Locate panel — content is server-rendered elsewhere on the page and
        // moved in below, so the markup stays in Liquid where it belongs and the
        // component only handles placement.
        + (this._tools.indexOf('locate') > -1
            ? '<div class="osa-panel" role="tabpanel" data-panel="locate" hidden>'
              + '<div class="osa-locate" data-role="locate-slot"></div>'
              + '</div>'
            : '')
        + '</section>'
        + '<section class="osr-region" data-osa="results-region" aria-live="polite">'
        +   '<div class="osv-card-slot" data-role="vehicle-card"></div>'
        +   '<oem-search-results surface="' + escapeHtml(this._surface) + '"></oem-search-results>'
        + '</section>';

      // Fill the locate panel from its <template>. Kept out of this file so the
      // copy and diagrams stay in Liquid, driven by the shop-wide metafield.
      if (this._locateSrc) {
        var src = document.getElementById(this._locateSrc);
        var slot = this.querySelector('[data-role="locate-slot"]');
        if (src && slot) {
          slot.appendChild(src.content ? src.content.cloneNode(true) : document.createTextNode(''));
          this._wireLocate(slot);
        }
      }

      this._results = this.querySelector('oem-search-results');
      this._vehicleCardEl = this.querySelector('[data-role="vehicle-card"]');
      // NB: the locate picker is [data-role="locate-make"], deliberately NOT
      // "make" — the line below would otherwise grab it on a locate-only
      // placement and the vehicle cascade would drive the wrong <select>.
      this._tabs = this.querySelector('[data-osa="tabs"]');
      this._makeEl = this.querySelector('[data-role="make"]');
      this._modelEl = this.querySelector('[data-role="model"]');
      this._yearEl = this.querySelector('[data-role="year"]');
      this._vehicleBtn = this.querySelector('[data-role="vehicle-search"]');
      this._qEl = this.querySelector('[data-role="q"]');
      this._colorBtn = this.querySelector('[data-role="color-search"]');
      this._plateEl = this.querySelector('[data-role="plate"]');
      this._stateEl = this.querySelector('[data-role="state"]');
      this._vinEl = this.querySelector('[data-role="vin"]');
      this._carBtn = this.querySelector('[data-role="car-search"]');
      this._carMode = this._plateEnabled ? 'plate' : 'vin';
      this._carScanEl = this.querySelector('[data-role="car-scan"]');
      this._carFileEl = this.querySelector('[data-role="car-file"]');
      this._footEl = this.querySelector('[data-osa="foot"]');
      this._carToggleEl = this.querySelector('[data-role="car-toggle"]');
      this._filtersEl = this.querySelector('[data-role="finish-filters"]');
    };

    /* ── wiring ─────────────────────────────────────────────────────── */
    OEMSearchApp.prototype._wire = function () {
      var self = this;

      // Tabs: real <button>s inside a delegated container. Native Enter/Space on
      // a focused tab dispatches a synthetic click that bubbles here — one code
      // path for mouse + keyboard, no separate keydown handler (Task 6 carry-item).
      this._tabs.addEventListener('click', function (e) {
        var tab = e.target.closest('.osa-tab');
        if (tab) self._selectTool(tab.getAttribute('data-tab'));
      });

      // (2) vehicle cascade
      this._makeEl.addEventListener('change', function () { self._onMakeChange(); });
      this._modelEl.addEventListener('change', function () { self._onModelChange(); });
      this._yearEl.addEventListener('change', function () { self._syncVehicleBtn(); });
      this._vehicleBtn.addEventListener('click', function () { self._runVehicle(); });

      // (3) color: debounced auto-search at >=2 chars, plus explicit button / Enter.
      var auto = debounce(function () {
        var q = self._qEl.value.trim();
        if (q.length >= 2) self._run({ tool: 'color', q: q });
      }, 300);
      this._qEl.addEventListener('input', auto);
      this._qEl.addEventListener('keydown', function (e) {
        // A text input has no native "click on Enter", so this is the only
        // activation path here — safe. Explicit search shares _run.
        if (e.key === 'Enter') { e.preventDefault(); var q = self._qEl.value.trim(); if (q) self._run({ tool: 'color', q: q }); }
      });
      this._colorBtn.addEventListener('click', function () {
        var q = self._qEl.value.trim(); if (q) self._run({ tool: 'color', q: q });
      });

      // (4) Your Car — [Plate | VIN] segmented toggle + one "Find my car" button.
      // The toggle swaps the visible sub-row; _runCar dispatches by the active mode.
      var toggles = this.querySelectorAll('[data-cartype]');
      for (var ci = 0; ci < toggles.length; ci++) {
        (function (btn) {
          btn.addEventListener('click', function () {
            var mode = btn.getAttribute('data-cartype');
            self._carMode = mode;
            var tg = self.querySelectorAll('[data-cartype]');
            for (var k = 0; k < tg.length; k++) {
              var on = tg[k] === btn;
              tg[k].classList.toggle('is-active', on);
              tg[k].setAttribute('aria-selected', on ? 'true' : 'false');
            }
            var subs = self.querySelectorAll('[data-carsub]');
            for (var s = 0; s < subs.length; s++) {
              var show = subs[s].getAttribute('data-carsub') === mode;
              subs[s].classList.toggle('is-active', show);
              subs[s].hidden = !show;
            }
            if (self._carScanEl) {
              var _lbl = self._carScanEl.querySelector('span');
              if (_lbl) _lbl.textContent = mode === 'vin' ? 'Scan VIN' : 'Scan plate';
            }
          });
        })(toggles[ci]);
      }
      if (this._plateEl) {
        this._plateEl.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); self._runCar(); } });
        this._stateEl.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); self._runCar(); } });
      }
      this._vinEl.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); self._runCar(); } });
      this._carBtn.addEventListener('click', function () { self._runCar(); });

      // (4c) OCR scan — one mobile-only button in the finish-chips row. Opens the camera,
      // OCRs the photo, fills the visible input (correctable) → decode. Scans plate or VIN
      // by the current toggle mode; hidden on desktop (CSS) and off the car tab (_selectTool).
      if (this._carScanEl) {
        this._carScanEl.addEventListener('click', function () { self._carFileEl.click(); });
        this._carFileEl.addEventListener('change', function () {
          if (this.files && this.files[0]) self._scan(self._carMode, this.files[0]);
          this.value = '';
        });
      }

      // (5) finish filter chips
      var chips = this.querySelectorAll('[data-filter]');
      for (var i = 0; i < chips.length; i++) {
        (function (chip) { chip.addEventListener('click', function () { self._toggleFilter(chip); }); })(chips[i]);
      }

      // (6) console foot: finish chips only while results are on screen (the
      // results element announces its state); the whole strip + separator
      // collapses when neither the toggle nor the chips are visible.
      this.addEventListener('oem-results-state', function (e) {
        if (self._filtersEl) self._filtersEl.hidden = !(e.detail && e.detail.count > 0);
        self._syncFoot();
      });
    };

    // Foot visibility = any of its children visible (else its border-top would
    // render as a stray divider under the console).
    OEMSearchApp.prototype._syncFoot = function () {
      if (!this._footEl) return;
      var toggleOn = !!(this._carToggleEl && !this._carToggleEl.hidden);
      var scanOn = !!(this._carScanEl && !this._carScanEl.hidden);
      var filtersOn = !!(this._filtersEl && !this._filtersEl.hidden);
      this._footEl.hidden = !toggleOn && !scanOn && !filtersOn;
    };

    /* ── tab / panel switching ──────────────────────────────────────── */
    /**
     * Make picker inside the "Where's My Code?" panel.
     *
     * The 22 makes share 4 location drawings and 16 plate drawings, so Liquid
     * renders each drawing ONCE and this swaps which pair is visible plus the
     * two captions. The data is the shop-wide metafield, serialised into the
     * panel as application/json — no request, and it works on placements with
     * no collection at all (the homepage).
     *
     * Unknown/blank make falls back to db.fallback, which points at the door
     * jamb and explicitly declines to guess a code format.
     */
    OEMSearchApp.prototype._wireLocate = function (slot) {
      var dataEl = slot.querySelector('[data-role="locate-data"]');
      var sel = slot.querySelector('[data-role="locate-make"]');
      if (!dataEl || !sel) return;

      var db;
      try { db = JSON.parse(dataEl.textContent || '{}'); } catch (e) { return; }

      var byMake = {};
      (db.brands || []).forEach(function (b) { byMake[b.make] = b; });

      var whereEl = slot.querySelector('[data-role="loc-where"]');
      var fmtEl = slot.querySelector('[data-role="loc-format"]');

      var show = function (attr, val) {
        var nodes = slot.querySelectorAll('[' + attr + ']');
        for (var i = 0; i < nodes.length; i++) {
          nodes[i].hidden = nodes[i].getAttribute(attr) !== val;
        }
      };

      var apply = function () {
        var b = byMake[sel.value] || db.fallback || {};
        show('data-diagram', b.diagram || 'doorjamb');
        show('data-plate', b.plate || 'generic');
        if (whereEl) whereEl.textContent = b.location || '';
        if (fmtEl) fmtEl.textContent = b.format || '';
      };

      sel.addEventListener('change', apply);
      apply(); // paint the seeded make (or the fallback) before first view
    };

    OEMSearchApp.prototype._selectTool = function (tool) {
      // legacy vin/plate URLs → the merged Your Car tab; anything unknown → vehicle
      if (tool !== 'vehicle' && tool !== 'color' && tool !== 'car' && tool !== 'locate') {
        tool = (tool === 'vin' || tool === 'plate') ? 'car' : 'vehicle';
      }
      // A tool this placement has switched off must not be selectable — otherwise
      // a stale data-default-tool or ?tool= URL lands on a tab that isn't rendered.
      if (this._tools && this._tools.indexOf(tool) < 0) tool = this._tools[0] || 'vehicle';
      // Task 20 (owner wave 2, #4): no plate tab/panel in the DOM when the
      // kill switch is off — fall back to vehicle so a stale data-default-
      // tool="plate" or a restored ?tool=plate URL never selects a
      // nonexistent tool.
      if (tool === 'plate' && !this._plateEnabled) tool = 'vehicle';
      this._activeTool = tool;
      if (this._vehicleCardEl) this._vehicleCardEl.innerHTML = ''; // stale vehicle card doesn't carry across tools
      var tabs = this.querySelectorAll('.osa-tab'), t, on;
      for (var i = 0; i < tabs.length; i++) {
        t = tabs[i]; on = t.getAttribute('data-tab') === tool;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) this._moveIndicator(t);
      }
      var panels = this.querySelectorAll('.osa-panel'), p;
      for (var j = 0; j < panels.length; j++) {
        p = panels[j]; on = p.getAttribute('data-panel') === tool;
        p.classList.toggle('is-active', on);
        p.hidden = !on;
      }
      // [Plate|VIN] toggle + scan chip live in the shared foot — car tab only.
      if (this._carToggleEl) this._carToggleEl.hidden = (tool !== 'car');
      if (this._carScanEl) this._carScanEl.hidden = (tool !== 'car');
      this._syncFoot();
      if (tool === 'vehicle') this._ensureMakes();   // populate make on first open
    };

    OEMSearchApp.prototype._moveIndicator = function (tab) {
      if (!this._tabs) return;
      this._tabs.style.setProperty('--osa-ind-left', tab.offsetLeft + 'px');
      this._tabs.style.setProperty('--osa-ind-width', tab.offsetWidth + 'px');
    };

    /* ── (2) vehicle cascade ────────────────────────────────────────── */
    OEMSearchApp.prototype._ensureMakes = function () {
      if (this._makesPromise) return this._makesPromise;
      var self = this;
      this._makesPromise = getMakes().then(function (makes) {
        self._makes = makes;
        self._makeEl.innerHTML = self._optionsHtml('Select Make', makes, 'brandId', 'brandName');
        return makes;
      }).catch(function (err) {
        console.error('[oem-search] getMakes failed', err);
        self._makesPromise = null; // allow retry
        return [];
      });
      return this._makesPromise;
    };

    OEMSearchApp.prototype._onMakeChange = function () {
      var self = this, brandId = this._makeEl.value;
      this._modelEl.innerHTML = '<option value="">Select Model</option>';
      this._modelEl.disabled = true;
      this._yearEl.innerHTML = '<option value="">Select Year</option>';
      this._yearEl.disabled = true;
      this._hasModels = true;
      this._syncVehicleBtn();
      if (!brandId) return;
      getModels(brandId).then(function (models) {
        if (self._makeEl.value !== brandId) return; // superseded
        self._hasModels = models.length > 0;
        if (models.length) {
          self._modelEl.innerHTML = self._optionsHtml('Select Model', models, 'modelId', 'modelName');
          self._modelEl.disabled = false;
        }
        // empty list → leave model disabled; Search stays enabled (brand-only search)
        self._syncVehicleBtn();
      }).catch(function (err) { console.error('[oem-search] getModels failed', err); });
    };

    OEMSearchApp.prototype._onModelChange = function () {
      var self = this, brandId = this._makeEl.value, modelId = this._modelEl.value;
      this._yearEl.innerHTML = '<option value="">Select Year</option>';
      this._yearEl.disabled = true;
      this._syncVehicleBtn();
      if (!brandId || !modelId) return;
      getYears(brandId, modelId).then(function (years) {
        if (self._modelEl.value !== modelId) return; // superseded
        if (!years.length) return;
        self._yearEl.innerHTML = self._yearsHtml(years);
        self._yearEl.disabled = false;
      }).catch(function (err) { console.error('[oem-search] getYears failed', err); });
    };

    OEMSearchApp.prototype._syncVehicleBtn = function () {
      this._vehicleBtn.disabled = !this._makeEl.value; // enabled once a Make is chosen (progressive)
    };

    OEMSearchApp.prototype._runVehicle = function () {
      var brandId = this._makeEl.value;
      if (!brandId) return;
      this._run({
        tool: 'vehicle', brandId: brandId,
        modelId: this._modelEl.value || '', year: this._yearEl.value || '',
        make: this._selectedText(this._makeEl), model: this._modelEl.value ? this._selectedText(this._modelEl) : ''
      });
    };

    /* ── (4) Your Car: plate/VIN → /api/vehicle/decode → card + colour results ── */
    OEMSearchApp.prototype._runPlate = function () {
      var self = this;
      var plate = this._plateEl.value.trim(), stateVal = this._stateEl.value;
      if (!plate || !stateVal) return;
      this._identify({ type: 'plate', plate: plate, state: stateVal }, function () { self._runPlate(); });
    };

    OEMSearchApp.prototype._runVin = function () {
      var self = this;
      var vin = (this._vinEl.value || '').trim().toUpperCase();
      if (vin.length < 11) return;
      this._identify({ type: 'vin', vin: vin }, function () { self._runVin(); });
    };

    // "Your Car" tab: run the active toggle side (plate default, or VIN).
    OEMSearchApp.prototype._runCar = function () {
      if (this._carMode === 'vin') this._runVin(); else this._runPlate();
    };

    // OCR a plate/VIN photo → fill the visible input(s) → decode. A misread lands
    // in the input (visible + editable), not silently searched.
    OEMSearchApp.prototype._scan = function (type, file) {
      var self = this;
      var reopen = function () { self._carFileEl.click(); };
      this._results.showLoading();
      var fd = new FormData();
      fd.append('type', type);
      fd.append('file', file);
      _fetch(config().vehicleOcrEndpoint, { method: 'POST', body: fd }).then(function (resp) {
        return resp.text().then(function (t) {
          var d = t ? JSON.parse(t) : {};
          if (!resp.ok) throw new Error(d.error || 'ocr failed');
          return d;
        });
      }).then(function (d) {
        if (type === 'plate' && d.plate) {
          if (self._plateEl) self._plateEl.value = d.plate;
          if (d.state && self._stateEl) self._stateEl.value = d.state;
          self._runPlate();
          return;
        }
        if (type === 'vin' && d.vin) {
          if (self._vinEl) self._vinEl.value = d.vin;
          self._runVin();
          return;
        }
        self._results.showError(reopen);
      }).catch(function (err) {
        console.error('[oem-search] OCR failed', err);
        self._results.showError(reopen);
      });
    };

    // Shared: decode via /api/vehicle/decode, render the "we found your vehicle" card,
    // then run the colour search — exact-colour-prioritized + badged (Partslink) or a
    // make/model/year fallback on the existing By-Vehicle path.
    OEMSearchApp.prototype._identify = function (payload, retryFn) {
      var self = this;
      this._results.showLoading();
      if (this._vehicleCardEl) this._vehicleCardEl.innerHTML = '';
      this._decodeCar(payload).then(function (data) {
        if (!data || !data.vin) { self._results.showError(retryFn); return; }
        if (self._vehicleCardEl) { self._vehicleCardEl.innerHTML = renderVehicleCard(data); self._lucide(); self._wirePalette(data); }
        var ps = data.partslink;
        if (ps && ps.query) {
          var v = ps.vehicle || {};
          var tok = String(v.colourName == null ? '' : v.colourName).toUpperCase().split(/\s+/)
            .filter(function (w) { return w.length >= 4 && !/^(METALLIC|MET|PEARL|MATT|MATTE|GLOSS|SOLID)$/.test(w); })
            .sort(function (a, b) { return b.length - a.length; })[0] || '';
          self._run({ tool: 'color', q: ps.query, prioritize: { code: v.colourCode, nameToken: tok } });
          return;
        }
        // Non-Partslink but the trim's OEM palette came back → the swatches in the card
        // are the CTA ("tap yours"); don't auto-search, just clear the spinner.
        if (data.colors && data.colors.length) { self._results.clear(); return; }
        // No palette either → make/model/year fallback (existing By-Vehicle search).
        if (data.make) {
          self._resolveBrandId(data.make).then(function (brandId) {
            if (brandId) self._run({ tool: 'vehicle', brandId: brandId, modelId: '', year: data.year ? String(data.year) : '', make: data.make, model: data.model || '' });
            else self._run({ tool: 'color', q: [data.make, data.model].filter(Boolean).join(' ') });
          });
          return;
        }
        self._results.showError(retryFn);
      }).catch(function (err) {
        console.error('[oem-search] vehicle decode failed', err);
        self._results.showError(retryFn);
      });
    };

    // Wire the OEM-palette swatches on a non-Partslink card: tap → search that colour.
    OEMSearchApp.prototype._wirePalette = function (data) {
      var self = this;
      if (!this._vehicleCardEl) return;
      var brand = data.make || '';
      var sws = this._vehicleCardEl.querySelectorAll('[data-pal-code]');
      for (var i = 0; i < sws.length; i++) {
        (function (btn) {
          btn.addEventListener('click', function () {
            var code = btn.getAttribute('data-pal-code'), name = btn.getAttribute('data-pal-name');
            var q = (name ? brand + ' ' + name : brand + ' ' + code).trim();
            self._run({ tool: 'color', q: q, prioritize: { code: code, nameToken: name } });
          });
        })(sws[i]);
      }
    };

    OEMSearchApp.prototype._decodeCar = function (payload) {
      return _fetch(config().vehicleDecodeEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', accept: 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (resp) {
        return resp.text().then(function (text) {
          var data = text ? JSON.parse(text) : {};
          if (resp.status === 404) return {};
          if (!resp.ok) throw new Error(data.error || 'decode failed');
          return data;
        });
      });
    };

    // getMakes() name match: case-insensitive exact first, then contains.
    OEMSearchApp.prototype._resolveBrandId = function (makeName) {
      return this._ensureMakes().then(function (makes) {
        makes = makes || [];
        var target = String(makeName || '').toLowerCase(), exact = '', contains = '';
        for (var i = 0; i < makes.length; i++) {
          var bn = String(makes[i].brandName || '').toLowerCase();
          if (!bn) continue;
          if (!exact && bn === target) exact = makes[i].brandId;
          if (!contains && (bn.indexOf(target) !== -1 || target.indexOf(bn) !== -1)) contains = makes[i].brandId;
        }
        return exact || contains || '';
      });
    };

    /* ── (5) finish filters ─────────────────────────────────────────── */
    OEMSearchApp.prototype._toggleFilter = function (chip) {
      var filter = chip.getAttribute('data-filter');
      var pressed = chip.getAttribute('aria-pressed') === 'true';
      chip.setAttribute('aria-pressed', pressed ? 'false' : 'true');
      chip.classList.toggle('is-active', !pressed);
      if (filter === 'metallic') this._filters.onlyMetallic = !pressed;
      if (filter === 'pearl') this._filters.onlyPearl = !pressed;
      if (this._lastParams) this._run(this._lastParams); // re-run last search with new filters
    };

    /* ── (6) run: merge filters, drive results, sync URL ────────────── */
    OEMSearchApp.prototype._run = function (params, opts) {
      opts = opts || {};
      this._lastParams = params; // base (sans filters) so filter toggles re-run cleanly

      // Task 20 (owner wave 2, #7b): "navigate" results mode (homepage-style
      // placement) sends the search to the dedicated search page instead of
      // rendering inline — reuses the same nativeSearchUrl/drawer-hook
      // routing as the header's nav-finder via openNative, so a
      // <oem-search-app data-results-mode="navigate"> behaves like a
      // shortcut into the full search experience. skipUrl calls (URL
      // restore) always fall through to the normal inline path below —
      // otherwise a page that both syncs the URL AND navigates would loop.
      if (this._resultsMode === 'navigate' && !opts.skipUrl) {
        openNative(params);
        return;
      }

      var merged = Object.assign({}, params, {
        onlyMetallic: this._filters.onlyMetallic,
        onlyPearl: this._filters.onlyPearl,
        page: 1
      });
      if (this._syncUrl && !opts.skipUrl) {
        // Filters aren't part of the URL contract, so a filter toggle recomputes
        // the SAME search string — only pushState when it actually changes to
        // avoid duplicate history entries (and popstate loops).
        var url = nativeSearchUrl(params);
        var qi = url.indexOf('?');
        var search = qi >= 0 ? url.slice(qi) : '';
        if (search !== this._lastAppliedSearch) {
          this._lastAppliedSearch = search;
          try { window.history.pushState({ oem: true }, '', url); } catch (e) { /* pushState may throw in sandboxes */ }
        }
      }
      var self = this;
      var p = runSearchInto(this._results, merged, this._state);
      if (p && typeof p.then === 'function') {
        p.then(function () {
          self._lucide();
          // Task 20 (owner wave 2, #7c): on a non-search-page placement (e.g.
          // homepage — data-sync-url="false"), the console usually sits above
          // the fold, so scroll the newly (re)rendered results into view.
          // Skipped on the dedicated search page (_syncUrl true) — the user
          // already navigated there to see results, nothing to reveal.
          if (!self._syncUrl) self._scrollResultsIntoView();
        });
      }
    };

    // Task 20 (owner wave 2, #7c): smooth-scroll, but honor
    // prefers-reduced-motion (falls back to an instant jump).
    OEMSearchApp.prototype._scrollResultsIntoView = function () {
      var region = this.querySelector('[data-osa="results-region"]');
      if (!region || typeof region.scrollIntoView !== 'function') return;
      var reduceMotion = typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      region.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    };

    /* ── (7) restore from URL ───────────────────────────────────────── */
    OEMSearchApp.prototype._restoreFromParams = function (params) {
      this._lastAppliedSearch = window.location.search || '';
      var tool = params.tool || ((params.brandId || params.make) ? 'vehicle' : (params.q ? 'color' : this._defaultTool));
      // Task 20 (owner wave 2, #4): resolve BEFORE the branches below run, not
      // just inside _selectTool — otherwise a restored ?tool=plate with the
      // switch off would visually land on the vehicle tab (via _selectTool's
      // own fallback) while this function's local `tool` var stayed 'plate',
      // so neither the color- nor vehicle-restore branch would fire.
      if (tool === 'plate' && !this._plateEnabled) tool = 'vehicle';
      this._selectTool(tool);
      if (tool === 'color') {
        this._qEl.value = params.q || '';
        if (params.q) this._run({ tool: 'color', q: params.q }, { skipUrl: true });
      } else if (tool === 'vehicle' && (params.brandId || params.make)) {
        this._restoreVehicle(params);
      }
      // tool === 'plate' with no decoded vehicle carries nothing to auto-run.
    };

    // Populate the cascades (await makes → models → years) then select saved
    // values, then run WITHOUT re-pushing URL (skipUrl) to keep history clean.
    OEMSearchApp.prototype._restoreVehicle = function (params, opts) {
      var self = this;
      var seedOnly = !!(opts && opts.seedOnly);
      var runParams = {
        tool: 'vehicle', brandId: params.brandId || '', modelId: params.modelId || '',
        year: params.year || '', make: params.make || '', model: params.model || ''
      };
      // seedOnly: populate the cascades and leave them for the customer to
      // finish. Used by the brand-page seed, where running a search on load
      // would duplicate the block already rendered above the finder.
      function finish() {
        self._syncVehicleBtn();
        if (!seedOnly) self._run(runParams, { skipUrl: true });
      }
      this._ensureMakes().then(function () {
        if (params.brandId) self._makeEl.value = params.brandId;
        else if (params.make) self._selectByText(self._makeEl, params.make);
        var brandId = self._makeEl.value;
        if (brandId && !runParams.brandId) runParams.brandId = brandId;
        if (!brandId) { finish(); return; }
        return getModels(brandId).then(function (models) {
          if (models.length) {
            self._modelEl.innerHTML = self._optionsHtml('Select Model', models, 'modelId', 'modelName');
            self._modelEl.disabled = false;
            if (params.modelId) self._modelEl.value = params.modelId;
          }
          if (!params.modelId || !self._modelEl.value) { finish(); return; }
          return getYears(brandId, self._modelEl.value).then(function (years) {
            if (years.length) {
              self._yearEl.innerHTML = self._yearsHtml(years);
              self._yearEl.disabled = false;
              if (params.year) self._yearEl.value = String(params.year);
            }
            finish();
          });
        });
      }).catch(function (err) { console.error('[oem-search] restore failed', err); finish(); });
    };

    /* ── small helpers ──────────────────────────────────────────────── */
    OEMSearchApp.prototype._optionsHtml = function (placeholder, list, idKey, nameKey) {
      var html = '<option value="">' + placeholder + '</option>';
      for (var i = 0; i < list.length; i++) {
        html += '<option value="' + escapeHtml(String(list[i][idKey])) + '">' + escapeHtml(String(list[i][nameKey])) + '</option>';
      }
      return html;
    };
    OEMSearchApp.prototype._yearsHtml = function (years) {
      var html = '<option value="">Select Year</option>';
      for (var i = 0; i < years.length; i++) {
        var y = escapeHtml(String(years[i]));
        html += '<option value="' + y + '">' + y + '</option>';
      }
      return html;
    };
    OEMSearchApp.prototype._selectedText = function (sel) {
      var o = sel.options[sel.selectedIndex];
      return o ? o.text : '';
    };
    OEMSearchApp.prototype._selectByText = function (sel, text) {
      var t = String(text || '').toLowerCase();
      for (var i = 0; i < sel.options.length; i++) {
        if (sel.options[i].text.toLowerCase() === t) { sel.selectedIndex = i; return true; }
      }
      return false;
    };
    OEMSearchApp.prototype._lucide = function () {
      if (window.lucide && typeof window.lucide.createIcons === 'function') {
        try { window.lucide.createIcons(); } catch (e) { /* icon lib optional */ }
      }
    };

    customElements.define('oem-search-app', OEMSearchApp);
  }

  /* ── Exports ─────────────────────────────────────────────────────── */
  var api = {
    config: config,
    deriveFinish: deriveFinish,
    pickEnglishColorName: pickEnglishColorName,
    normalizeHit: normalizeHit,
    gidToNumericId: gidToNumericId,
    buildProductUrl: buildProductUrl,
    buildFallbackUrl: buildFallbackUrl,
    buildSearchPageUrl: buildSearchPageUrl,
    paramsFromSearch: paramsFromSearch,
    searchColors: searchColors,
    getMakes: getMakes,
    getModels: getModels,
    getYears: getYears,
    resolveHandles: resolveHandles,
    resolveProductUrl: resolveProductUrl,
    renderResultsHTML: renderResultsHTML,
    renderVehicleCard: renderVehicleCard,
    renderStateHTML: renderStateHTML,
    finishInfoDialogHTML: finishInfoDialogHTML,
    FINISH_INFO: FINISH_INFO,
    priceLineHTML: priceLineHTML,
    escapeHtml: escapeHtml,
    debounce: debounce,
    nativeSearchUrl: nativeSearchUrl,
    openNative: openNative,
    filterVariantRows: filterVariantRows,
    runSearchInto: runSearchInto,
    prioritizeColors: prioritizeColors,
    _setFetch: _setFetch,
    clearApiCache: clearApiCache
  };
  if (isBrowser) window.OEMSearchEngine = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
